import React, { useState } from 'react';
import cn from 'classnames';
import { faDownload } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';

import { Table } from '../../../BrandReports/GraphTypes/Table';
import BarGraph from '../../../BrandReports/GraphTypes/BarGraph';
import Loader from '../../../General/Loader';

import { getBrandReportHeaderLabel } from '../../../../helpers/brand_helpers';

const ConciergeOrdersAndVolume = props => {
  const { conciergeVolume, title, groupingMethod, downloading } = props;
  const success = !!conciergeVolume?.length;

  const [tab, setTab] = useState('table'); // graph, table
  const download_table = async () => await props.download({ rows: make_table_rows(), fileName: 'concierge_orders_and_volume' });
  const download_graph = async () => await props.download({ graph: title });

  const make_table_rows = () => {
    let orders_running_total = 0;
    const orders = {
      key: 'orders_total',
      label: 'Orders',
      description: '(orders driven to all brands)',
      values: conciergeVolume.map(result => {
        const { week, month, quarter, orders } = result;
        orders_running_total += parseInt(orders || 0);
        return { label: getBrandReportHeaderLabel({ groupingMethod, week, month, quarter }), value: parseInt(orders) };
      })
    };
    orders.values.push({ label: 'Total', value: orders_running_total });

    let volume_running_total = 0;
    const volume = {
      key: 'volume_total',
      label: 'Volume',
      description: `(revenue driven to all brands)`,
      values: conciergeVolume.map(result => {
        const { week, month, quarter, volume } = result;
        volume_running_total += parseInt(volume || 0);
        return { label: getBrandReportHeaderLabel({ groupingMethod, week, month, quarter }), value: `$${parseInt(volume) || 0}` };
      })
    };
    volume.values.push({ label: 'Total', value: `$${volume_running_total?.toFixed()}` });

    let domains_running_total = 0;
    const unique_domains = {
      key: 'unique_domains_total',
      label: 'Unique Domains',
      description: `(number of domains orders came from)`,
      values: conciergeVolume.map(result => {
        const { week, month, quarter, unique_domains } = result;
        domains_running_total += parseInt(unique_domains || 0);
        return { label: getBrandReportHeaderLabel({ groupingMethod, week, month, quarter }), value: unique_domains };
      })
    };
    unique_domains.values.push({ label: 'Total', value: domains_running_total });

    return [orders, volume, unique_domains];
  };

  return (
    <div className='talent-report-card' id={title}>
      {!conciergeVolume ? (
        <Loader />
      ) : (
        <>
          {success && (
            <>
              <div className='header'>
                <div className='description'>
                  <div className='title'>{title}</div>
                  <div className='sub-title'>
                    Orders and Volume are defined as the orders for all pins that we were able to track orders for. The volume is the sum of the
                    revenue for all orders.
                  </div>
                </div>
                {!downloading && (
                  <div className='talent-report-controls' id={`${title}-card-controls`}>
                    <div className='radio-btns'>
                      <div className={cn('radio-btn', { active: tab === 'graph' })} onClick={() => setTab('graph')}>
                        Graph
                      </div>
                      <div className={cn('radio-btn', { active: tab === 'table' })} onClick={() => setTab('table')}>
                        Table
                      </div>
                    </div>
                    <div className='rounded-button' onClick={() => (tab === 'table' ? download_table() : download_graph())}>
                      {downloading ? (
                        <Loader />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faDownload}></FontAwesomeIcon>
                          Download
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {tab === 'table' && <Table title='Orders and Volume' data={make_table_rows()} />}
              {tab === 'graph' && (
                <BarGraph
                  title='Volume'
                  data={conciergeVolume.map(click => {
                    const { week, month, quarter, year, volume } = click;
                    const data = {
                      key: `${week || month || quarter}${year}volume`,
                      value: volume,
                      label: getBrandReportHeaderLabel({ groupingMethod, week, month, quarter })
                    };
                    return data;
                  })}
                />
              )}
            </>
          )}

          {!success && (
            <>
              <div className='error-centered'>
                <div className='title'>Error Fetching {title}</div>
                <div className='sub-title'>No results.</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

ConciergeOrdersAndVolume.propTypes = {
  title: PropTypes.string.isRequired,
  conciergeVolume: PropTypes.array,
  groupingMethod: PropTypes.string.isRequired,

  download: PropTypes.func.isRequired,
  downloading: PropTypes.bool.isRequired
};

const ConciergeOrdersAndVolumeConfig = {
  title: 'Volume'
};

export { ConciergeOrdersAndVolume, ConciergeOrdersAndVolumeConfig };
